/****************************************************************
 * jotai state and atoms for the accounts listing
 ***************************************************************/

import { atom, useSetAtom } from 'jotai';
import { atomWithReset, atomWithStorage, useResetAtom } from 'jotai/utils';
import callApi from 'lib/callApi';
import { companyIdAtom } from 'views/state';

import { DEFAULT_PAGE_SIZE } from '../types';

export const DEFAULT_REPOSITORY_LISTING_COLUMNS = [
  'repo_branch',
  'account',
  'status',
  'vulnerabilities',
  'last_commit_date',
  'scan__last_sync_completed_at',
  'actions',
];
export const enabledColumnKeysAtom = atomWithStorage(
  'sast_repos_enabled_column_keys',
  DEFAULT_REPOSITORY_LISTING_COLUMNS,
);

export const selectedRowIdsAtom = atomWithReset<number[]>([]);
export const forceReloadRepoDataAtom = atom<string>('');

export const paginationConfigAtom = atomWithReset({
  page_size: DEFAULT_PAGE_SIZE,
  page: 1,
});
export const orderbyAtom = atomWithReset<string>('updated_at');
export const sortOrderAtom = atomWithReset<'desc' | 'asc'>('desc');

// Additional filtering
export const selectedAccountIdListAtom = atomWithReset<number[]>([]);
export const selectedHostTypeListAtom = atomWithReset<string[]>([]);
export const selectedStateFiltersListAtom = atomWithReset<string[]>([]);

// Store the combined count of vulnerabilities and bugs for each checked repository
export const checkedRepoVulnAndBugsMapAtom = atom<Record<number, number>>({});

// Calculate total vulnerabilities and bugs across all checked repositories
export const totalVulnAndBugsForCheckedReposAtom = atom(async (get) => {
  const checkedRepoIds = get(selectedRowIdsAtom);
  const vulnAndBugsMap = get(checkedRepoVulnAndBugsMapAtom);
  let total = 0;

  if (checkedRepoIds?.length > 0) {
    const allRepos = get(repositoriesDataAtom);

    // Update counts with any new repositories from current page
    const currentPageRepos = allRepos?.results?.filter((repo) =>
      checkedRepoIds.includes(repo.id),
    );

    // Calculate total using both stored and current page values
    checkedRepoIds.forEach((repoId) => {
      // Use stored value if available, otherwise 0
      total += vulnAndBugsMap[repoId] || 0;
    });

    // Update totals with any new values from current page
    currentPageRepos?.forEach((repo) => {
      const totalIssues =
        (repo.scan?.synced_vuln_count || 0) +
        (repo.scan?.synced_bugs_count || 0);
      if (totalIssues !== vulnAndBugsMap[repo.id]) {
        total = total - (vulnAndBugsMap[repo.id] || 0) + totalIssues;
      }
    });
  }
  return total;
});

export const repositoriesDataAtom = atom(async (get) => {
  const companyId = get(companyIdAtom);
  const paginationConfig = get(paginationConfigAtom);

  const selectedAccountIdsList = get(selectedAccountIdListAtom);
  const selectedHostTypesList = get(selectedHostTypeListAtom);
  const selectedStateFilters = get(selectedStateFiltersListAtom);

  const orderBy = get(orderbyAtom);
  const order = get(sortOrderAtom);
  const ordering = `${order === 'desc' ? '-' : ''}${orderBy}`;

  try {
    if (companyId && companyId !== -1) {
      const params = {
        company_id: companyId,
        page: paginationConfig.page, // Specify the page number you want to retrieve
        page_size: paginationConfig.page_size, // Specify the number of items per page
        ordering: ordering, // Specify the field to sort by and the sorting order
        account_ids: selectedAccountIdsList.join(','), // Serialize array to comma-separated string
        host_types: selectedHostTypesList.join(','), // Serialize array to comma-separated string
        state_filters: selectedStateFilters.join(','), // Serialize array to comma-separated string
      };

      const responseData = await callApi('sast/repositories/', 'GET', params);
      return responseData?.data;
    }
  } catch (error) {}
  return undefined;
});

export const forceRefreshStatusAtom = atom<string>('');
export const forceRerenderUIAtom = atom<string>('');
export const repositoriesStatusDataAtom = atom(async (get) => {
  const companyId = get(companyIdAtom);
  const forceRefresh = get(forceRefreshStatusAtom);
  const repositoriesData = get(repositoriesDataAtom);

  try {
    // only load on force refresh
    if (
      companyId &&
      companyId !== -1 &&
      repositoriesData?.results &&
      forceRefresh
    ) {
      const params = {
        company_id: companyId,
        repo_ids: repositoriesData?.results.map((i) => i.id).join(','),
      };

      const responseData = await callApi(
        'sast/repositories/status',
        'GET',
        params,
      );

      return responseData?.data;
    }
  } catch (error) {}
  return undefined;
});

/**
 * reset the repository page state
 */
export function useRepositoryResetAtoms() {
  const resetPaginationConfigAtom = useResetAtom(paginationConfigAtom);
  const resetOrderbyAtom = useResetAtom(orderbyAtom);
  const resetOrderAtom = useResetAtom(sortOrderAtom);
  const setReposForceRefresh = useSetAtom(forceReloadRepoDataAtom);
  const resetSelectedAccountIdList = useResetAtom(selectedAccountIdListAtom);
  const resetSelectedHostTypeList = useResetAtom(selectedHostTypeListAtom);
  const resetSelectedStateFiltersList = useResetAtom(
    selectedStateFiltersListAtom,
  );
  const resetSelectedRowIds = useResetAtom(selectedRowIdsAtom);

  const resetAtoms = () => {
    resetPaginationConfigAtom();
    resetOrderbyAtom();
    resetOrderAtom();
    setReposForceRefresh(Math.random().toString());
    resetSelectedAccountIdList();
    resetSelectedHostTypeList();
    resetSelectedStateFiltersList();
    resetSelectedRowIds();
  };

  return resetAtoms;
}
